import { z } from 'zod';
import { defineStepType } from '../contract.js';
import { fileCategorySchema } from '../../files/categories.js';
import { CALENDAR_DATE, endOfDayInAgencyZone, isPastInAgencyZone, startOfDayInAgencyZone, } from '../../time/agency-dates.js';
/**
 * A licence or certification: the card itself, plus the number and dates on it.
 *
 * `document-upload` proves a file arrived and carries only a nullable expiry with no
 * identifier, so it cannot feed `expiry.mode: 'credential-date'` with a licence number a
 * surveyor can trace back to a state registry. Thirteen person-owned credential rows gate a
 * first assignment and all of them need this.
 *
 * Dates are collected and STORED as calendar dates — the thing printed on the card — and
 * converted to instants only where the engine needs one. Converting on ingest would look
 * tidier and would break re-parsing: `runtime.approve` re-runs `submissionSchema` over the
 * STORED submission, so a schema that transformed a date into an instant would reject its
 * own output the second time it saw it. Keeping the typed value also means the audit answers
 * "what did they read off the card", which an instant cannot.
 */
const calendarDate = z.string().regex(CALENDAR_DATE, 'expected a YYYY-MM-DD date');
export const dateCredential = defineStepType({
    type: 'date-credential',
    /** The person supplies the credential document and its dates. */
    submittedBy: 'person',
    label: 'Licence or certification',
    description: 'Collects the credential document with its number and dates, and mints a tracked credential on approval.',
    configSchema: z.object({
        heading: z.string().min(1),
        instructions: z.string().default(''),
        /** What the number is called on this card — "License number", "Certification ID". */
        identifierLabel: z.string().min(1).default('License number'),
        requiresIdentifier: z.boolean().default(true),
        collectsIssueDate: z.boolean().default(false),
        /**
         * Whether an expiry date must be supplied. Defaults to TRUE, and the default is the
         * decision.
         *
         * `fromCredentials` returns `indeterminate` with `impossible_dates` for a credential
         * with no expiry against an `expiry.mode: 'credential-date'` rule. So allowing a blank
         * expiry there means the person completes the step, HR approves it, and the row sits at
         * "Needs review" for ever with nothing on screen explaining why — the same shape as the
         * Q27 gap that stranded every registry-qualified aide.
         *
         * Opting out is therefore explicit and rare: a credential that genuinely never expires,
         * on a requirement whose expiry mode is not `credential-date`. A seed-fidelity test
         * refuses the dangerous combination outright.
         */
        requiresExpiry: z.boolean().default(true),
        acceptedTypes: z.array(z.string().min(1)).min(1),
        /**
         * CEILING MATCHED TO WHAT THE SERVER WILL SERVE (#239). It was 50 against a 20 MB serving
         * cap, so a published config could accept a 40 MB upload that is then refused at download
         * with an over-cap alarm — stored, filed, and unreadable. The two numbers have to move
         * together; this one may not be raised without raising the serving cap first.
         */
        maxSizeMb: z.number().int().positive().max(20).default(20),
        target: fileCategorySchema,
        /** Which requirement the minted credential closes. Checked against `satisfies` at seed. */
        requirementKey: z.string().min(1).nullable().default(null),
    }),
    submissionSchema: z.object({
        identifier: z.string().min(1).max(120).nullable().default(null),
        issuedOn: calendarDate.nullable().default(null),
        expiresOn: calendarDate.nullable().default(null),
        uploadId: z.string().min(1),
        fileName: z.string().min(1),
    }),
    /** The credential's scan. Always exactly one. */
    uploads: ({ submission }) => [submission.uploadId],
    onApprove: ({ instance, config, submission }) => [
        {
            kind: 'store-file',
            instanceId: instance.id,
            uploadId: submission.uploadId,
            category: config.target,
            source: 'portal-upload',
            // Pinned at enqueue, where the upload is in hand; a step type has no upload to plan for.
            plan: null,
        },
        ...(config.requirementKey !== null
            ? [
                {
                    kind: 'mint-credential',
                    personId: instance.personId,
                    requirementKey: config.requirementKey,
                    identifier: submission.identifier,
                    // The card's dates, as instants. An issue date is a lower bound so it goes to the
                    // START of its day; an expiry is valid THROUGH its day so it goes to the end.
                    // `fromCredentials` reads `expiresAt <= issuedAt` as impossible dates, so a card
                    // issued and expiring on one day must not trip that.
                    issuedAt: submission.issuedOn === null ? null : startOfDayInAgencyZone(submission.issuedOn),
                    expiresAt: submission.expiresOn === null ? null : endOfDayInAgencyZone(submission.expiresOn),
                },
            ]
            : []),
        { kind: 'recompute-rollup', personId: instance.personId },
    ],
    expiresAt: ({ submission }) => submission.expiresOn === null ? null : endOfDayInAgencyZone(submission.expiresOn),
    checkSubmission: async ({ config, submission, now }) => {
        if (config.requiresIdentifier && (submission.identifier ?? '').trim() === '') {
            return { ok: false, message: `Please enter the ${config.identifierLabel.toLowerCase()} from your card.` };
        }
        if (config.requiresExpiry && submission.expiresOn === null) {
            // Refused here rather than minting a credential the engine can only read as
            // indeterminate. A person who has done everything asked of them must not be left on
            // "Needs review" with no way to find out why.
            return {
                ok: false,
                message: 'Please enter the expiry date printed on this document.',
            };
        }
        if (submission.expiresOn !== null && isPastInAgencyZone(submission.expiresOn, now)) {
            // Refused here rather than approved and left to read `expired` on the checklist. A
            // credential that lands expired is not evidence of anything, and telling somebody now
            // is far cheaper than HR discovering it during a review.
            return {
                ok: false,
                message: 'That expiry date has already passed. Please send the current version of this document.',
            };
        }
        if (submission.issuedOn !== null &&
            submission.expiresOn !== null &&
            submission.expiresOn < submission.issuedOn) {
            // Both are YYYY-MM-DD, so a plain string comparison is a date comparison here.
            return { ok: false, message: 'The expiry date is before the issue date. Please check both.' };
        }
        return { ok: true };
    },
});
//# sourceMappingURL=date-credential.js.map